import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchFrame, fetchFrames, createFrame, updateFrame, reorderFrames } from "src/actions/mine_craft/framesActions";

export interface FrameInterface {
  url: string;

  id: number;
  app_id: number;

  image_url: string;
  frame_type: 'static' | 'animated';
  order: number;
  coins: number;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null

  newParams: FrameInterface | undefined;
  newError: string | null;

  newGroupParams: FrameInterface | undefined;
  newGroupError: string | null;

  reorderIds: number[] | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,

  newGroupParams: undefined,
  newGroupError: null,

  reorderIds: null,
};

const adaptor = createEntityAdapter<FrameInterface>();

const slice = createSlice({
  name: 'frames',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      const defaults = {}

      state.newParams = action.payload ? { ...defaults, ...action.payload } : undefined;
      state.newError  = initialState.newError;
    },
    setNewGroupParams: (state, action) => {
      state.newGroupParams = action.payload;
      state.newGroupError  = initialState.newGroupError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
    setReorderIds: (state, action) => {
      state.reorderIds = action.payload.ids;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchFrames.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchFrames.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.frames;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchFrames.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #create
      .addCase(createFrame.fulfilled, (state, action) => {
        const frame = Object.values(action.payload.frame)[0];
        state.entities[frame.id] = frame;
      })
      .addCase(createFrame.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchFrame.fulfilled, (state, action) => {
        const frame = Object.values(action.payload.frame)[0];
        state.entities[frame.id] = frame;
      })

      // #update
      .addCase(updateFrame.fulfilled, (state, action) => {
        const frame = Object.values(action.payload.frame)[0];
        state.entities[frame.id] = frame;
      })
      .addCase(updateFrame.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #reorder
      .addCase(reorderFrames.fulfilled, (state, action) => {
        state.ids = action.payload.ids;
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewGroupParams,
  setNewError,
  setEditError,
  setReorderIds
} = slice.actions;

export const {
  selectById: selectFrameById,
  selectIds: selectFrameIds,
  selectEntities: selectFrameEntities,
}  = adaptor.getSelectors<RootState>(state => state.mineCraft.frames);
